import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import i18next from 'i18next';

import { OrderHeaderResponse } from 'types/api/orders.type';

import { useDeleteOrderMutation } from 'redux/api/ordersApi';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { setOrderId, setOrderNumber } from 'redux/slices/settings';

import { ordersDataService } from 'services';

import { BucketContext } from 'contexts';

import { downloadOrderExport } from 'helpers/downloadOrderExport';
import { downloadOrderPdf } from 'helpers/downloadOrderPdf';
import { getOrderAvailablePriceTypes } from 'helpers/pricing';

import { DuplicateOrderModal } from 'components/DuplicateOrderModal';
import { Icon } from 'components/Icon';
import { InfoModal } from 'components/InfoModal';
import { PriceTypeModal, PriceTypeOption } from 'components/PriceTypeModal';

import * as S from './ActionsDropdown.styles';

type ActionsDropdownProps = {
  orderId: string;
  orderNumber: string;
  status: string;
  customerCode?: string;
  customerName?: string;
};

const ActionsDropdown = ({
  orderId,
  orderNumber,
  status,
  customerCode,
  customerName,
}: ActionsDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isDuplicateModalVisible, setIsDuplicateModalVisible] = useState(false);
  const [priceTypeModalAction, setPriceTypeModalAction] = useState<'pdf' | 'export' | null>(
    null
  );
  // The row knows no prices, so the order is loaded when the price-type modal
  // opens: the modal then offers only the types the order resolves, and the
  // PDF reuses the loaded order instead of fetching it again.
  const [modalOrder, setModalOrder] = useState<OrderHeaderResponse>();
  const [isModalOrderLoading, setIsModalOrderLoading] = useState(false);
  const modalPriceTypes = useMemo(
    () => (modalOrder ? getOrderAvailablePriceTypes(modalOrder.items ?? []) : undefined),
    [modalOrder]
  );
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { getUrl } = useContext(BucketContext);
  const { orderNumber: currentOrderNumber } = useAppSelector((state) => state.settings);
  const dispatch = useAppDispatch();
  const isSend = status === 'SEND';

  const [deleteOrderMutation] = useDeleteOrderMutation();

  const handleToggle = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen((prev) => !prev);
  }, []);

  const close = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        close();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, close]);

  const openPriceTypeModal = async (action: 'pdf' | 'export') => {
    setPriceTypeModalAction(action);
    setModalOrder(undefined);
    setIsModalOrderLoading(true);
    try {
      setModalOrder((await ordersDataService.getOrderById(orderId)).data);
    } catch (err) {
      // Without the order the modal falls back to offering every type.
      console.error('[List] getOrderById for the price-type modal failed:', err);
    } finally {
      setIsModalOrderLoading(false);
    }
  };

  const handleDownloadPdf = (e: React.MouseEvent) => {
    e.stopPropagation();
    close();
    openPriceTypeModal('pdf');
  };

  const handleExport = (e: React.MouseEvent) => {
    e.stopPropagation();
    close();
    openPriceTypeModal('export');
  };

  const handlePriceTypeConfirm = async (selectedType: PriceTypeOption) => {
    const action = priceTypeModalAction;
    setPriceTypeModalAction(null);
    const priceType = selectedType === 'NO_PRICE' ? undefined : selectedType;

    if (action === 'pdf') {
      try {
        await downloadOrderPdf({ orderId, header: modalOrder, priceType });
      } catch (err) {
        console.error('[List PDF] downloadOrderPdf failed:', err);
        toast.error(t('order.downloadFailed'));
      }
    } else if (action === 'export') {
      try {
        await downloadOrderExport({
          orderId,
          lang: i18next.language,
          orderNumber,
          getUrl,
          priceType,
        });
      } catch {
        toast.error(t('order.exportFailed'));
      }
    }
  };

  const handleDuplicate = (e: React.MouseEvent) => {
    e.stopPropagation();
    close();
    setIsDuplicateModalVisible(true);
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    close();
    setIsDeleteModalVisible(true);
  };

  const deleteOrder = async () => {
    try {
      await deleteOrderMutation(orderId).unwrap();

      if (currentOrderNumber === orderNumber) {
        localStorage.setItem('orderNumber', '');
        localStorage.setItem('orderId', '');
        dispatch(setOrderNumber(''));
        dispatch(setOrderId(''));
      }

      toast.success(t('order.deletedSuccessfully'));
    } catch (error) {
      console.error(error);
      toast.error(t('order.deleteFailed'));
    }
    setIsDeleteModalVisible(false);
  };

  return (
    <>
      <S.Wrapper ref={wrapperRef}>
        <S.ToggleButton type="button" onClick={handleToggle} aria-label={t('common.actions')}>
          <Icon name="FiMoreVertical" size={18} />
        </S.ToggleButton>

        {isOpen && (
          <S.Menu>
            <S.MenuItem type="button" onClick={handleDownloadPdf}>
              <Icon name="FiPrinter" size={14} />
              {t('pdf.download')}
            </S.MenuItem>

            <S.MenuItem type="button" onClick={handleExport}>
              <Icon name="FiDownload" size={14} />
              {t('selection.export')}
            </S.MenuItem>

            <S.MenuItem type="button" onClick={handleDuplicate} disabled={!customerCode}>
              <Icon name="FiCopy" size={14} />
              {t('common.copy')}
            </S.MenuItem>

            <S.MenuItem type="button" onClick={handleDeleteClick} disabled={isSend} $danger>
              <Icon name="FiX" size={14} />
              {t('common.delete')}
            </S.MenuItem>
          </S.Menu>
        )}
      </S.Wrapper>

      {isDeleteModalVisible && (
        <InfoModal
          title={t('order.deleteConfirmTitle')}
          info={t('order.deleteConfirm')}
          onClose={() => setIsDeleteModalVisible(false)}
          onAction={deleteOrder}
          actionText={t('common.delete')}
        />
      )}

      {isDuplicateModalVisible && customerCode && (
        <DuplicateOrderModal
          orderId={orderId}
          customerCode={customerCode}
          customerName={customerName}
          onClose={() => setIsDuplicateModalVisible(false)}
        />
      )}

      {priceTypeModalAction && (
        <PriceTypeModal
          onClose={() => setPriceTypeModalAction(null)}
          onConfirm={handlePriceTypeConfirm}
          isLoading={isModalOrderLoading}
          availableTypes={modalPriceTypes}
        />
      )}
    </>
  );
};

export { ActionsDropdown };
